import { useEffect, useState } from 'react';
import { ThemeProvider } from './providers/theme-provider';
import { AppShell } from './layout/AppShell';
import { LoginPage } from './auth/LoginPage';
import { useAuthStore } from './auth/authStore';
import { initLayerRegistry } from './map/layers/registry';
import { getCachedToken } from './map/portal';
import { startDispatcherBridge } from './agent/dispatcher-bridge';
import { useGlobalKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useChatStore } from './chat/chatStore';

type AuthStatus =
  | { kind: 'pending' }
  | { kind: 'ok' }
  | { kind: 'error'; message: string };

export function App() {
  const authenticated = useAuthStore((s) => s.authenticated);

  return (
    <ThemeProvider>
      {authenticated ? <AuthenticatedApp /> : <LoginPage />}
    </ThemeProvider>
  );
}

function AuthenticatedApp() {
  const [auth, setAuth] = useState<AuthStatus>({ kind: 'pending' });
  useGlobalKeyboardShortcuts();
  const bootstrapChatOnLaunch = useChatStore((s) => s.bootstrapOnLaunch);

  useEffect(() => {
    let cancelled = false;
    const stopBridge = startDispatcherBridge();
    void initLayerRegistry();

    const runBootstrap = () => {
      if (!cancelled) bootstrapChatOnLaunch();
    };
    let unsubHydration: (() => void) | undefined;
    if (useChatStore.persist.hasHydrated()) {
      runBootstrap();
    } else {
      unsubHydration = useChatStore.persist.onFinishHydration(runBootstrap);
    }

    (async () => {
      try {
        await getCachedToken();
        if (!cancelled) setAuth({ kind: 'ok' });
      } catch (err) {
        if (!cancelled) setAuth({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
      unsubHydration?.();
      stopBridge();
    };
  }, [bootstrapChatOnLaunch]);

  return <AppShell auth={auth} />;
}
